import React, { useEffect, useRef, useState } from "react"
import { Button, Card, Collapse, Stack, Table } from "react-bootstrap"
import PageTitleButton from "../../components/PageTitleButton"
import BookingDetails from "./BookingDetails"
import {
  useHandleUnauthorizedAccess,
  getStatusClassName,
  HasModulePermission,
} from "../../lib/GlobalMethods"
import { toast } from "react-toastify"
import recordNotFound from "../../assets/images/record_not_found.png"
import BookingManagementSkeleton from "../../skeleton/bookingManagement"
import { admin } from "../../config/apiEndpoints"
import axiosProvider from "../../lib/AxiosProvider"
import useDebounce from "../../lib/hooks/useDebounce"
import InfiniteScroll from "react-infinite-scroll-component"
import moment from "moment"
import SearchBox from "../../components/SearchBox"
import CustomSelect from "../../components/CustomSelect"
import { modulePermission } from "../../lib/GlobalVariables"
import TooltipComponent from "../../components/Tooltip"
import BookingCancellation from "./BookingCancellation"
import ExportingStationData from "./ExportingStationData"
import { useSelector } from "react-redux"

const BookingManagement = () => {
  const { userDetails } = useSelector((state) => state.basicInformation)
  const roleName = userDetails?.role?.trim().toLowerCase()
  const isVendor = roleName === "vendor" || roleName === "fleet vendor"
  const roleResolved = typeof userDetails?.role === "string"
  const hasDeletePermission = HasModulePermission(
    modulePermission.booking,
    "delete",
  )
  const canDelete = !isVendor && hasDeletePermission

  const [data, setData] = useState(null)
  const [show, setShow] = useState(false)
  const [showExportCsvModal, setShowExportCsvModal] = useState(false)
  const [isView, setIsView] = useState(false)
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: false,
  })
  const [isUpdate, setIsUpdate] = useState(false)
  const [recordsCount, setRecordsCount] = useState({
    total_records: false,
    active_records: false,
    archived_records: false,
  })
  const [searchQuery, setSearchQuery] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [open, setOpen] = useState(false)
  const [filterObj, setFilterObj] = useState({
    status: false,
    stationId: "",
    fleetClientId: "",
  })
  const [cancellation, setCancellation] = useState(false)

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const { handleAccess } = useHandleUnauthorizedAccess()

  const handleShow = () => setShow(true)
  const handleClose = () => {
    setShow(false)
    setCancellation(false)
    setShowExportCsvModal(false)
  }
  const [chargingStationDetails, setChargingStationDetails] = useState([])
  const [fleetClientDetails, setFleetClientDetails] = useState([])
  const pendingRequests = useRef(new Set())

  const fetchData = async (searchQuery) => {
    const searchParams = new URLSearchParams({
      page: pagination.current_page,
      search_term: searchQuery || "",
      status: filterObj.status,
    })
    if (!isVendor) {
      searchParams.set("station_id", filterObj.stationId)
      searchParams.set("fleet_client_id", filterObj.fleetClientId)
    }
    const requestKey = `${admin.bookingList}?${searchParams.toString()}`
    if (pendingRequests.current.has(requestKey)) return
    pendingRequests.current.add(requestKey)

    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: requestKey,
      })

      const responseData = response?.data || response?.response?.data
      if (responseData?.code === 429) {
        toast.error(responseData.message || "Too many requests")
        setLoading(false)
        return
      }

      const fetchedData = responseData?.data || []
      setHasMore(true)

      handleAccess({ code: responseData?.code })

      responseData?.code === 200 && setPagination(responseData?.pagination)
      responseData?.code === 200 &&
        setRecordsCount(responseData?.record_counts)

      if (
        !fetchedData.length ||
        responseData?.pagination?.current_page ===
          responseData?.pagination?.total_pages
      ) {
        setHasMore(false)
      }

      setData((prevData) =>
        pagination.current_page === 1
          ? fetchedData
          : [...prevData, ...fetchedData],
      )

      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error.message || "An error occurred")
    } finally {
      pendingRequests.current.delete(requestKey)
    }
  }

  const fetchMoreData = () => {
    if (pagination.current_page < pagination.total_pages) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: prevState.current_page + 1,
      }))
    } else {
      setHasMore(false)
    }
  }

  const filterOnClick = () => {
    setOpen(!open)
  }

  const statusOptions = [
    { label: "Pending", value: "Pending" },
    { label: "Ongoing", value: "Ongoing" },
    { label: "Confirmed", value: "Confirmed" },
    { label: "Completed", value: "Completed" },
    { label: "Cancelled", value: "Cancelled" },
  ]

  const getStationDetails = async () => {
    const requestKey = admin.chargingStationList
    if (pendingRequests.current.has(requestKey)) return
    pendingRequests.current.add(requestKey)
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: requestKey,
      })

      if (response?.data?.code === 200) {
        const formattedData = response?.data?.data?.map((items) => ({
          label: items?.station_name,
          value: items?.id,
        }))

        setChargingStationDetails(formattedData)
      }
    } catch (error) {
      console.error(error)
    } finally {
      pendingRequests.current.delete(requestKey)
    }
  }

  const getFleetClientDetails = async () => {
    const requestKey = `${admin.fleetClientList}?page=1&limit=200`
    if (pendingRequests.current.has(requestKey)) return
    pendingRequests.current.add(requestKey)
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: requestKey,
      })

      if (response?.data?.code === 200) {
        const formattedData = response?.data?.data
          ?.map((client) => ({
            label: client?.name,
            value: client?.id,
          }))
          .filter(
            (client) =>
              client.value !== undefined && client.label !== undefined,
          )

        setFleetClientDetails(formattedData)
      }
    } catch (error) {
      console.error(error)
    } finally {
      pendingRequests.current.delete(requestKey)
    }
  }

  useEffect(() => {
    if (!roleResolved || isVendor) return
    getStationDetails()
    getFleetClientDetails()
  }, [roleResolved, isVendor])

  useEffect(() => {
    if (!roleResolved) return
    fetchData(debouncedSearchQuery)
  }, [debouncedSearchQuery, pagination.current_page, filterObj, roleResolved])

  return (
    <>
      <PageTitleButton
        PagetitleText={"Booking Management"}
        filterShow={true}
        filterClick={filterOnClick}
        open={open}
      />

      <Collapse in={open}>
        <div id="brand-filter-collapse" className="filters_sticky">
          <Card className="mb-3">
            <Card.Body>
              <Stack direction="horizontal" gap={2} className="flex-wrap">
                <div className="d-flex gap-2 align-items-center justify-content-between flex-wrap">
                  <div className="d-inline-flex gap-2 align-items-center">
                    <SearchBox
                      value={searchQuery}
                      placeholder="Search booking ID, charger serial number or station name"
                      onChange={(e) => {
                        setSearchQuery(e?.target?.value)
                        setPagination({ current_page: 1 })
                        setLoading(true)
                      }}
                    />
                  </div>
                </div>
                <CustomSelect
                  options={statusOptions}
                  handleSelectChange={(selectedStatus) => {
                    setFilterObj((prev) => ({
                      ...prev,
                      status: selectedStatus ? selectedStatus.label : false,
                    }))
                    setPagination({ current_page: 1 })
                    setLoading(true)
                  }}
                  isMulti={false}
                  isSearchable={false}
                  placeholder={"Select Booking Status"}
                  classNameSelect={"selectdropdown_filter"}
                />
                {!isVendor && userDetails?.dataScope !== "OWN" && (
                  <CustomSelect
                    options={chargingStationDetails}
                    handleSelectChange={(selectedStationId) => {
                      setFilterObj((prev) => ({
                        ...prev,
                        stationId: selectedStationId
                          ? selectedStationId.value
                          : "",
                      }))
                      setLoading(true)
                    }}
                    isMulti={false}
                    isSearchable={true}
                    placeholder={"Select Charging Staion"}
                    classNameSelect={"selectdropdown_filter"}
                  />
                )}
                {!isVendor && (
                  <CustomSelect
                    options={fleetClientDetails}
                    handleSelectChange={(selectedFleetClient) => {
                      setFilterObj((prev) => ({
                        ...prev,
                        fleetClientId: selectedFleetClient
                          ? selectedFleetClient.value
                          : "",
                      }))
                      setPagination({ current_page: 1 })
                      setLoading(true)
                    }}
                    isMulti={false}
                    isSearchable={true}
                    placeholder={"Select Fleet Client"}
                    classNameSelect={"selectdropdown_filter"}
                  />
                )}

                <button
                  className="d-inline-flex gap-2 align-items-center ms-auto btn btn-primary"
                  onClick={() => setShowExportCsvModal(true)}
                >
                  <i className="icon_main download_icon" />
                  Export CSV
                </button>
              </Stack>
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {!loading ? (
        <Card>
          <Card.Body>
            <InfiniteScroll
              dataLength={data?.length || 10}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <BookingManagementSkeleton infiniteScroll={true} rows={1} />
              }
            >
              <Table
                className="rounded mb-0 table-striped overflow-x-auto"
                responsive
              >
                <thead>
                  <tr>
                    <th className="text-center">Id</th>
                    <th className="text-center">Serial Number</th>
                    <th>User Details</th>
                    <th className="text-center">Station Name</th>
                    <th className="text-center">Connector Type</th>
                    <th className="text-center">Status</th>
                    <th className="text-center lh-1">Slot time</th>
                    <th className="text-center">Amount</th>
                    <th className="text-center">Payment method</th>
                    {canDelete && <th className="text-center">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {data?.length > 0 &&
                    data?.map((item, index) => (
                      <tr key={index + 1}>
                        <td
                          className="text-center text-primary"
                          onClick={() => {
                            handleShow()
                            setIsView(item)
                          }}
                          role="button"
                          tabIndex="0"
                        >
                          {item?.id}
                        </td>
                        <td className="text-center">
                          <p className="mb-0 lh-sm fs_14">
                            {item?.charger?.id}
                          </p>
                        </td>
                        <td>
                          <p className="mb-0 lh-1 text-nowrap">
                            {item?.user?.name}
                          </p>
                          <span className="fs_14 text_7gray text-nowrap">
                            {item?.user?.phone}
                          </span>
                        </td>
                        <td className="text-center">
                          <p className="mb-0 lh-sm fs_14">
                            {item?.station?.station_name}
                          </p>
                        </td>
                        <td>
                          <div className="sppted_connectors">
                            <div className="connector_item position-relative">
                              <span
                                className="bg-body border border-opacity-75 d-inline-flex justify-content-center align-items-center fs_14 p-1 rounded-circle text-body text-uppercase position-absolute end-0"
                                style={{ width: "22px", height: "22px" }}
                              >
                                {item?.charger_position
                                  ?.charAt(0)
                                  .toUpperCase()}
                              </span>
                              <img
                                src={
                                  item?.connector?.connector_img
                                    ? process.env.REACT_APP_RESOURCE_URL +
                                      item?.connector?.connector_img
                                    : "https://placehold.co/50x50.png"
                                }
                                alt="connector_img"
                                className="connector_item_img"
                              />
                              <span className="connector_nm">
                                {item?.connector?.connector_type}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                          <span
                            className={`badges ${getStatusClassName(
                              item.status,
                            )} fs_14 fw-normal px-3 py-1`}
                          >
                            {item?.status}
                          </span>
                        </td>
                        <td className="text-center fs_14">
                          <p className="mb-0 text_7gray lh-1">
                            {moment(item?.booked_start_time).format(
                              "YYYY-MM-DD",
                            )}
                          </p>
                          <span className="mb-0 text-uppercase fw-semibold">
                            {moment(item?.booked_start_time).format("hh:mm A")}
                          </span>
                        </td>
                        <td className="text-center">
                          ₹{item?.bookingwise_payment?.amount || 0}/-
                        </td>
                        <td className="text-center">
                          {item?.bookingwise_payment?.payment_method || "-"}
                        </td>
                        {canDelete && (
                          <td className="text-center">
                            {item?.status === "Confirmed" ? (
                              <div className="d-inline-flex gap-2">
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText="Cancel"
                                >
                                  <Button
                                    variant="btndelete_icon"
                                    className="btn_icon"
                                    onClick={() => {
                                      setIsUpdate(item)
                                      setCancellation(true)
                                    }}
                                  >
                                    <i className="icon_main cross_circle"></i>
                                  </Button>
                                </TooltipComponent>
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  {data && !data?.length && (
                    <tr>
                      <td className="text-center py-4" colSpan={9}>
                        <div className="norecord_wrapper">
                          <img src={recordNotFound} alt="No Record Found" />
                          <p className="norecords_title">No Records Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </InfiniteScroll>
          </Card.Body>
        </Card>
      ) : (
        <BookingManagementSkeleton />
      )}

      <BookingDetails show={show} handleClose={handleClose} isView={isView} />

      <BookingCancellation
        show={cancellation}
        setData={setData}
        handleClose={handleClose}
        isUpdate={isUpdate}
        setIsUpdate={setIsUpdate}
        fetchData={fetchData}
      />

      <ExportingStationData
        show={showExportCsvModal}
        handleClose={handleClose}
        setShow={setShowExportCsvModal}
        chargingStationId={filterObj?.stationId}
        fleetClientId={filterObj?.fleetClientId}
        isVendor={isVendor}
        roleResolved={roleResolved}
      />
    </>
  )
}

export default BookingManagement
